import { useTranslation } from 'react-i18next';
import { Chip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import DataGrid from '../../shared/DataGrid/StyledDataGrid';
import React, { useMemo } from 'react';
import {
  CAMPAIGNS_WRITE,
  LOYALTY_WRITE,
  PRODUCTS_WRITE,
  SHOPS_WRITE,
  usePermission,
} from '../../shared/utils/permissions';
import { Route, Routes } from 'react-router-dom';
import {
  useAllIngredients,
  useAllOutOfStockIngredients,
  useAllOutOfStockProducts,
  useAllProducts,
} from '../../apis/products-api';
import { OutOfStockProductsDetails } from './OutOfStockProductDetails';
import { OutOfStockIngredientDto, OutOfStockProductDto } from '@kotipizzagroup/kotipizza-products-api-client';
import dayjs from 'dayjs';
import { DataGridContainer, TopBar } from '../IngredientsPage';
import { StyledRouterLink } from '../../shared/StyledLink/StyledLink';
import { OutOfStockIngredientDetails } from './OutOfStockIngredientDetails';
import NewOutOfStockItemSearch from './NewOutOfStockItemSearch';
import { useAllRestaurantsWithDetails } from '../../apis/shops-api';
import { UserRole } from '@kotipizzagroup/kotipizza-users-api-client';
import { isNotArchived, isScheduleInEffect } from '../../shared/utils/outOfStockFilters';

export type OutOfStockRow = {
  item: OutOfStockProductDto | OutOfStockIngredientDto;
  itemId: number;
  restaurantName?: string;
};

type OutOfStockList = {
  [key: number]: OutOfStockRow[];
};

type ListData = {
  id: number;
  name: string;
  outOfStocks: OutOfStockRow[];
  isIngredient: boolean;
};

export const OutOfStockPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: outOfStockProducts, isLoading: isOutOfStockProductsLoading } = useAllOutOfStockProducts();
  const { data: outOfStockIngredients, isLoading: isOutOfStockIngredientsLoading } = useAllOutOfStockIngredients();
  const { data: products, isLoading: isProductsLoading } = useAllProducts(false);
  const { data: ingredients, isLoading: isIngredientsLoading } = useAllIngredients(false);
  const { data: restaurants, isLoading: isRestaurantsLoading } = useAllRestaurantsWithDetails();

  const isAdminUser = usePermission([PRODUCTS_WRITE, LOYALTY_WRITE, SHOPS_WRITE, CAMPAIGNS_WRITE]);
  const userAuthorizesToViewOutOfStock = usePermission([UserRole.OutOfStock_CanView]) || isAdminUser;
  const userAuthorizesToUpdateOutOfStock = usePermission([UserRole.OutOfStock_CanUpdate]) || isAdminUser;

  const isLoading =
    isOutOfStockProductsLoading ||
    isOutOfStockIngredientsLoading ||
    isProductsLoading ||
    isIngredientsLoading ||
    isRestaurantsLoading;

  const list = useMemo((): ListData[] => {
    let combinedList: ListData[] = [];
    if (isLoading) return combinedList;

    // Look up restaurant names by id once instead of scanning the full restaurant list per row.
    const restaurantNameById = new Map((restaurants ?? []).map((r) => [r.externalId, r.displayName]));

    if (outOfStockIngredients) {
      const outOfStocksByIngredientId: OutOfStockList = outOfStockIngredients.reduce<OutOfStockList>(
        (prev, current) => {
          if (current.ingredientId) {
            const masterIngredient = ingredients?.find((x) => x.ingredientId == current.ingredientId);
            if (masterIngredient && !isNotArchived(masterIngredient)) {
              return prev;
            }
            if (!isScheduleInEffect(current)) {
              return prev;
            }
            if (!prev[current.ingredientId]) {
              prev[current.ingredientId] = [];
            }
            prev[current.ingredientId].push({
              item: current,
              itemId: current.ingredientId,
              restaurantName: restaurantNameById.get(current.externalShopId),
            });
          }
          return prev;
        },
        {}
      );

      // One row per ingredient — ingredients sharing a warehouse item are intentionally shown
      // separately here so each keeps its own per-restaurant out-of-stock entries.
      combinedList = Object.keys(outOfStocksByIngredientId)
        .map((key) => ({
          id: Number(key),
          name: ingredients?.find((i) => i.ingredientId === Number(key))?.name || '',
          outOfStocks: outOfStocksByIngredientId[Number(key)],
          isIngredient: true,
        }))
        .filter((ing) => ing.outOfStocks.length > 0);
    }

    if (outOfStockProducts && userAuthorizesToViewOutOfStock) {
      const outOfStocksByProductId: OutOfStockList = outOfStockProducts.reduce<OutOfStockList>((a, product) => {
        if (product.productId) {
          const masterProduct = products?.find((p) => p.productId === product.productId);
          if (masterProduct && !isNotArchived(masterProduct)) {
            return a;
          }
          if (!isScheduleInEffect(product)) {
            return a;
          }
          if (!a[product.productId]) {
            a[product.productId] = [];
          }
          a[product.productId].push({
            item: product,
            itemId: product.productId,
            restaurantName: restaurantNameById.get(product.externalShopId),
          });
        }
        return a;
      }, {});

      combinedList.push(
        ...Object.keys(outOfStocksByProductId)
          .map((key) => ({
            id: Number(key),
            name: products?.find((p) => p.productId === Number(key))?.name || '',
            outOfStocks: outOfStocksByProductId[Number(key)],
            isIngredient: false,
          }))
          .filter((item) => item.outOfStocks.length > 0)
      );
    }

    return combinedList;
  }, [
    ingredients,
    isLoading,
    outOfStockIngredients,
    outOfStockProducts,
    products,
    restaurants,
    userAuthorizesToViewOutOfStock,
  ]);

  const columns: GridColDef<ListData>[] = [
    {
      field: 'id',
      headerName: t('Out of stock product'),
      flex: 1,
      renderCell: (params) => {
        if (!params.row.name) return null;
        const name = `${params.row.name}${params.row.isIngredient ? ` (${t('ingredient')})` : ` (${t('product')})`}`;

        return (
          <StyledRouterLink to={`/out-of-stock/${params.row.isIngredient ? 'ingredient' : 'product'}/${params.row.id}`}>
            {name}
          </StyledRouterLink>
        );
      },
    },
    {
      field: 'externalShopId',
      headerName: t('Out of stock from restaurant'),
      flex: 2,
      renderCell: (params) => {
        const restaurantAmount = params.row.outOfStocks.length;
        if (restaurantAmount > 4) {
          return <div>{`${restaurantAmount} ${t('global.restaurant', { count: restaurantAmount })}`}</div>;
        }

        return params.row.outOfStocks.map((o) => <Chip label={o.restaurantName} />);
      },
    },
    {
      field: 'added',
      headerName: t('Marked out of stock at'),
      flex: 1,
      renderCell: (params) => {
        const latestAddition = params.row.outOfStocks.sort((a: OutOfStockRow, b: OutOfStockRow) => {
          return dayjs(a.item.added).isBefore(dayjs(b.item.added)) ? 1 : -1;
        })[0];

        return <div>{dayjs(latestAddition.item.added).format('D.M.YYYY [klo] HH:mm')}</div>;
      },
    },
    {
      field: 'edit',
      headerName: t('Actions'),
      renderCell: (params) => {
        return (
          userAuthorizesToUpdateOutOfStock && (
            <div>
              <StyledRouterLink
                key={params.row.id + '-edit'}
                to={`/out-of-stock/${params.row.isIngredient ? 'ingredient' : 'product'}/${params.row.id}`}
              >
                {t('edit')}
              </StyledRouterLink>
            </div>
          )
        );
      },
    },
  ];

  return (
    <Routes>
      <Route path="/ingredient/:id" element={<OutOfStockIngredientDetails />} />
      <Route path="/product/:id" element={<OutOfStockProductsDetails />} />
      <Route
        index
        element={
          <>
            <TopBar>
              <Typography variant="h1">{t('Out of stock products')}</Typography>
              <NewOutOfStockItemSearch />
            </TopBar>
            <DataGridContainer display="flex">
              <DataGrid
                disableRowSelectionOnClick
                rows={list}
                columns={columns}
                getRowId={(row) => row.id}
                loading={isLoading}
              />
            </DataGridContainer>
          </>
        }
      />
    </Routes>
  );
};

export default OutOfStockPage;
